import { useTranslation } from '@lyearn/i18n';
import { AlertCircleFill } from '@lyearn/icons';
import { Stack, Typography } from '@lyearn/ui';
import QuestionComment from '@/modules/user-survey/questions/components/QuestionComment';

import { QUESTION_PLAYER_ERROR_DATA_ATTRIBUTE } from './consts';
import { QuestionPlayerContainerProps } from './types';

const CONFIG = {
  form: {
    questionText: { variant: 'heading-xs' },
    description: { variant: 'body-short-01' },
    comments: { className: 'mt-24' },
  },
  player: {
    questionText: { variant: 'heading-l' },
    description: { variant: 'body-short-02' },
    comments: { className: 'mt-16' },
  },
} as const;

const QuestionPlayerContainer = (props: React.PropsWithChildren<QuestionPlayerContainerProps>) => {
  const {
    className = 'py-20',
    variant,
    children,
    question,
    userSurveyQuestionSettings,
    error,
    post,
    onPostChange,
    useRichTextForComment = true,
    commentTitle,
    requireComments,
    disabled,
  } = props;

  const { t } = useTranslation();
  const { description, questionText, settings } = question;

  const config = CONFIG[variant];

  const renderComments = () => {
    if (!userSurveyQuestionSettings?.allowComments) {
      return null;
    }

    return (
      <QuestionComment
        className={config.comments.className}
        commentTitle={commentTitle}
        disableComments={disabled}
        post={post}
        requireComments={requireComments}
        useRichTextForComment={useRichTextForComment}
        onPostChange={onPostChange}
      />
    );
  };

  const errorDataAttributes = error ? { [QUESTION_PLAYER_ERROR_DATA_ATTRIBUTE]: error } : {};

  return (
    <Stack className={className} direction="col">
      {variant === 'player' ? (
        error ? (
          <Stack alignItems="center" className="mb-8" gapX={6} {...errorDataAttributes}>
            <AlertCircleFill className="h-16 w-16" fillColorToken="icon-error" />
            <Typography color="text-text-error" variant="body-short-02">
              {error}
            </Typography>
          </Stack>
        ) : (
          <Stack className="mb-8 h-22 ps-8" />
        )
      ) : null}

      <Typography className="ps-8" color="text-text-primary" variant={config.questionText.variant}>
        {questionText}{' '}
        {userSurveyQuestionSettings?.isOptional ? (
          <Typography as="span" color="text-text-placeholder" variant="body-short-02">
            {t('UserSurvey:Optional')}
          </Typography>
        ) : null}
      </Typography>

      {settings?.enableDescription && description ? (
        <Typography
          className="mt-8 ps-8"
          color="text-text-secondary"
          variant={config.description.variant}>
          {description}
        </Typography>
      ) : null}

      {children}

      {error && variant === 'form' ? (
        <Typography
          className="mt-8 ps-8"
          color="text-text-error"
          variant="body-short-03"
          {...errorDataAttributes}>
          {error}
        </Typography>
      ) : null}

      {renderComments()}
    </Stack>
  );
};

export default QuestionPlayerContainer;
